import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { TEuDdpDeliveryOperator } from 'redux/reducers/euDdp/types';
import { searchDeliveryOperatorsRequest } from 'redux/reducers/euDdp/reducer';
import {
	selectDeliveryOperatorsSearch,
	selectDeliveryOperatorsSearchLoading,
} from 'redux/reducers/euDdp/selectors';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { tableStructure as defaultTableStructure } from './tableStructure';
import { DeliveryOperatorDialog } from './DeliveryOperatorDialog';

const EDIT_ACTION = 'Edit';

export const EuDdpDeliveryOperators: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [operatorToEdit, setOperatorToEdit] = useState<TEuDdpDeliveryOperator | null>(null);

	const operatorsData = useSelector(selectDeliveryOperatorsSearch);
	const operatorsLoading = useSelector(selectDeliveryOperatorsSearchLoading);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TEuDdpDeliveryOperator>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const loadOperators = useCallback(() => {
		const params = queryString.parse(search);
		const filters = getFiltersFromNavigateParams(currentTableStructure, params) as TFilters;

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(
			searchDeliveryOperatorsRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
				orderBy,
			}),
		);
	}, [currentTableStructure, dispatch, search]);

	useEffect(() => {
		loadOperators();
	}, [loadOperators]);

	const handleAddNew = useCallback(() => {
		setOperatorToEdit(null);
		setDialogOpen(true);
	}, []);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action === EDIT_ACTION) {
				const row = operatorsData?.data?.find((o) => o.id === id) ?? null;
				setOperatorToEdit(row);
				setDialogOpen(true);
			}
		},
		[operatorsData],
	);

	const handleDialogClose = useCallback(() => {
		setDialogOpen(false);
		setOperatorToEdit(null);
		loadOperators();
	}, [loadOperators]);

	return (
		<>
			<DeliveryOperatorDialog
				open={dialogOpen}
				onClose={handleDialogClose}
				operatorToEdit={operatorToEdit}
			/>
			<EasTableWithFilter<TEuDdpDeliveryOperator>
				tableStructure={currentTableStructure}
				items={operatorsData}
				itemsIsLoading={operatorsLoading}
				handleAction={handleAction}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Resources / Reference Tables / EU DDP / <b>Delivery Operators</b>
					</Typography>
					<Button variant="contained" color="primary" size="small" onClick={handleAddNew}>
						Add New Delivery Operator
					</Button>
				</Box>
			</EasTableWithFilter>
		</>
	);
};
